import { observer } from 'mobx-react';
import { ReactNode, useEffect, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { DropDownProps } from 'antd/lib/dropdown';

// Components
import {
    FileSearchOutlined,
    HistoryOutlined,
    HomeOutlined,
    LogoutOutlined,
    ShareAltOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { Dropdown, Menu, notification } from 'top-component-library';

// Constants
import breakpoints from 'constants/breakpoints';

// Hooks
import useFacebookAuth from 'hooks/use-facebook-auth';
import { useStores } from 'hooks/use-stores';

// Utils
import { retrieveFromLocalStorage } from 'utils/LocalStorage';

// Tracking
import { userNavigationTrackingEvents } from 'integrations/segment/tracking-events';
import { getLastLocationFromLocalStorage } from 'utils/History';

function AccountDropdown({ className }: { className?: string }) {
  const facebookAuth = useFacebookAuth();
  const history = useHistory();
  const location = useLocation();
  const { locationCode } = useParams<{ locationCode: string }>();
  const [isAuthenticated, setIsAuthenticated] = useState(retrieveFromLocalStorage('isAuthenticated'));
  const { hostStore, userStore, tabStore, locationStore } = useStores();
  const showExternalLoyalty = hostStore?.external_loyalty_integration?.integration_connected ?? false;
  const { landing_page_sign_in_text, menu_sign_in_text, show_account_sign_in } = hostStore?.settings ?? {};
  const {
    trackClickSignOut,
    trackClickAccountDetails,
    trackClickOrderHistory,
    trackClickManageTabs,
    trackClickReturnToHome,
    trackClickAccountDropDown,
    trackClickSignIn,
  } = userNavigationTrackingEvents;

  if (locationStore.kiosk_enabled) {
    return null;
  }

  useEffect(() => {
    setIsAuthenticated(retrieveFromLocalStorage('isAuthenticated'));
  }, [userStore.user_info]);

  const logout = async (): Promise<void> => {
    trackClickSignOut();
    if (userStore.user_info.login_type === 'facebook') {
      try {
        await facebookAuth.logout();
        await logoutLocal();
      } catch (err) {
        console.error(err);
        notification.error({
          message: 'Unable to log out due to a Facebook error. Please refresh and try again.',
        });
      }
    } else {
      await logoutLocal();
    }
  };

  const logoutLocal = async (): Promise<void> => {
    try {
      await userStore.logout();
      history.push(getLastLocationFromLocalStorage());
      notification.success({
        message: 'You have successfully logged out.',
      });
    } catch (err) {
      console.error(err);
      notification.error({
        message: 'Unable to log out due to a network error. Please refresh and try again.',
      });
    }
  };

  const shareConsumerTab = async (): Promise<void> => {
    const data = await tabStore.getShareURL(locationCode);
    if (data.url) {
      window.navigator.share({
        title: 'Share Tab',
        text: "You're invited to join a Tab!",
        url: data.url,
      });
    } else {
      notification.error({ message: data.message });
    }
  };

  const DropdownMenu = (
    <Menu>
      <MenuItem key="account-details" data-testid="account-details">
        <Link to="/account-details" onClick={() => trackClickAccountDetails()}>
          <UserOutlined />
          <span>Account Details</span>
        </Link>
      </MenuItem>
      <MenuItem key="order-history" data-testid="order-history">
        <Link to="/order-history" onClick={() => trackClickOrderHistory()}>
          <HistoryOutlined />
          <span>Order History</span>
        </Link>
      </MenuItem>
      <MenuItem key="party-tabs" data-testid="party-tabs">
        <Link to="/party-tabs" onClick={() => trackClickManageTabs()}>
          <FileSearchOutlined />
          <span>Manage Tabs</span>
        </Link>
      </MenuItem>
      {!!tabStore.activeConsumerTab && !!window.navigator?.share && (
        <MenuItem key="share-tab" data-testid="share-tab" onClick={shareConsumerTab}>
          <ShareAltOutlined />
          <span>Share Tab</span>
        </MenuItem>
      )}
      <MenuItem key="home" data-testid="home">
        <Link to="/" onClick={() => trackClickReturnToHome()}>
          <HomeOutlined />
          <span>Return to Home</span>
        </Link>
      </MenuItem>
      <MenuItem key="sign-out" data-testid="sign-out" onClick={logout}>
        <LogoutOutlined />
        <span>Sign Out</span>
      </MenuItem>
    </Menu>
  );

  const getSignInText = (): string => {
    if (locationCode && menu_sign_in_text) {
      // This text is apparently configurable, but "sign in" is the default (specified in railbot). So if it's still the default, format it
      return menu_sign_in_text === 'sign in' ? 'Sign In' : menu_sign_in_text;
    } else if (!locationCode && landing_page_sign_in_text) {
      return landing_page_sign_in_text;
    } else {
      return 'Sign In';
    }
  };

  if (show_account_sign_in === false || showExternalLoyalty) {
    return null;
  }

  return (
    <>
      {isAuthenticated ? (
        <DropdownContainer
          data-testid="account-dropdown"
          className={`${className ?? ''} account-dropdown`}
          overlay={DropdownMenu}
          trigger={['click']}
          onVisibleChange={(visible) => trackClickAccountDropDown({ visible })}
        >
          <IconBorder className="account-dropdown-icon-border">
            <Icon data-testid="profile-icon" className="account-dropdown-icon" />
          </IconBorder>
        </DropdownContainer>
      ) : (
        // Do not show "Sign In" link on login/registration pages.
        !location.pathname.includes('/login') &&
        !location.pathname.includes('/register') &&
        !showExternalLoyalty && (
          <LoginLink
            className={`login-link account-dropdown-sign-in-text ${className ?? ''} `}
            data-testid="account-sign-in"
            to="/login"
            onClick={() => trackClickSignIn()}
          >
            {getSignInText()}
          </LoginLink>
        )
      )}
    </>
  );
}

export default observer(AccountDropdown);

interface DropdownContainerProps extends DropDownProps {
  'data-testid'?: string;
  children?: ReactNode;
}

const DropdownContainer = styled(Dropdown)<DropdownContainerProps>`
  align-items: center;
  background-color: transparent;
  cursor: pointer;
  display: flex;
  justify-content: center;
  position: relative;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: var(--color-neutral__page-background);
  }
`;

const Icon = styled(UserOutlined)`
  color: var(--color-neutral__body);
  font-size: 0.75rem;
`;

const IconBorder = styled.div`
  border-radius: 100%;
  border: 1px solid var(--color-neutral__body);
  box-shadow: none;
  height: 0.5rem;
  margin-left: 0.75rem;
  padding: 0.5rem;
  transform-origin: right;
  transform: scale(1.125); // Use transform to keep icon border width consistent with icon stroke width.
  width: 0.5rem;

  @media (min-width: ${breakpoints.md}px) {
    &&& {
      margin-right: 2rem;
      transform: scale(1.625);
    }
  }
`;

const LoginLink = styled(Link)`
  color: var(--color-neutral__body);
  font-size: 0.75rem;
  letter-spacing: 0.25px;

  &:focus,
  &:hover {
    color: var(--color-neutral__body);
    opacity: 0.8;
  }

  &:focus-visible {
    text-decoration: underline;
    outline: 2px solid var(--color-primary__regular);
  }

  @media (min-width: ${breakpoints.md}px) {
    font-size: 1rem;
    letter-spacing: 0.75px;
  }
`;

const MenuItem = styled(Menu.Item)`
  align-items: center;
  display: flex;

  &:not(:last-of-type) {
    border-bottom: 1px solid var(--color-neutral__line);
  }

  a {
    align-items: center;
    display: flex;
  }
`;
